import { useEffect, useState } from "react";
import { UpdateCompanyDepartmentModalProps } from "./UpdateCompanyDepartmentModal";
import {
    CompanyDepartmentFormState_default,
    CompanyDepartmentFormState_fromDepartment,
    CompanyDepartmentFormState_toRequest
} from "../../types/forms/CompanyDepartmentFormState";
import { getCompanyDepartmentByIdAsync, updateCompanyDepartmentAsync } from "../../apiClient";
import { showDangerAlert } from "../../common";

function useUpdateCompanyDepartmentModal(props: UpdateCompanyDepartmentModalProps) {
    const [formState, setFormState] = useState(CompanyDepartmentFormState_default());
    const [isLoading, setIsLoading] = useState(true);
    const [updateRequestInProgress, setUpdateRequestInProgress] = useState(false);

    const disableSubmit = !formState.name.value.trim() || isLoading || updateRequestInProgress;

    useEffect(() => {
        async function fetchDepartmentAsync() {
            const fetchResult = await getCompanyDepartmentByIdAsync(props.departmentId);

            if (fetchResult.failed) {
                console.error("Failed to fetch company department with id", props.departmentId, "Error:", fetchResult.error);
                setIsLoading(false);
                showDangerAlert(props.resources.get("FetchCompanyDepartmentError") ?? "");
                props.onHide();
                return;
            }

            setFormState(CompanyDepartmentFormState_fromDepartment(fetchResult.value));
            setIsLoading(false);
        }

        if (props.show) {
            fetchDepartmentAsync();
        }
    }, [props.departmentId, props.resources, props.onHide, props.show]);

    async function handleSubmitAsync(e: React.FormEvent) {
        e.preventDefault();
        const request = CompanyDepartmentFormState_toRequest(formState);

        setUpdateRequestInProgress(true);
        const result = await updateCompanyDepartmentAsync(props.departmentId, request);
        setUpdateRequestInProgress(false);

        if (result.failed) {
            console.error(
                "Failed to update company department. DepartmentId:",
                props.departmentId,
                "Request:",
                request,
                "Error:",
                result.error
            );
            const nameErrors = result.error.errors["Name"];
            const nameErrorMessage = nameErrors && nameErrors.length > 0 ? nameErrors[0] : undefined;

            if (!nameErrorMessage) {
                showDangerAlert(props.resources.get("UpdateCompanyDepartmentError") ?? "");
                return;
            }

            setFormState((prevState) => ({
                ...prevState,
                name: {
                    ...prevState.name,
                    errorMessage: nameErrorMessage
                }
            }));

            return;
        }

        props.onDepartmentUpdated(props.departmentId);
    }

    function handleNameChange(name: string) {
        setFormState((prevState) => ({
            ...prevState,
            name: { value: name, errorMessage: undefined }
        }));
    }

    function handleIsEnergyHarbourCustomerChange(isChecked: boolean) {
        setFormState((prevState) => ({
            ...prevState,
            isEnergyHarbourCustomer: { value: isChecked, errorMessage: undefined }
        }));
    }

    return {
        formState,
        isLoading,
        disableSubmit,
        handleNameChange,
        handleIsEnergyHarbourCustomerChange,
        handleSubmitAsync
    };
}

export default useUpdateCompanyDepartmentModal;
